import { Target, Package, Search, Youtube, Download, Code, ShieldCheck, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import FAQ from "@/components/FAQ";
import CTABanner from "@/components/CTABanner";

const IMG = "https://images.unsplash.com/photo-1687125114671-d45714b4631d?crop=entropy&cs=srgb&fm=jpg&q=85";

const CAPABILITIES = [
  {
    icon: Target,
    title: "Search Campaign Precision",
    copy: "Tightly themed ad groups, exact-match discipline and match-type ladders that keep budget pointed at buying intent.",
  },
  {
    icon: Package,
    title: "Performance Max Asset Optimization",
    copy: "Feeding PMax the asset variety and signals it needs — then steering it with audience data instead of hoping.",
  },
  {
    icon: Search,
    title: "High-Intent Keyword Mining",
    copy: "Continuous query excavation: we find the terms that convert, isolate them and bid like they matter. Because they do.",
  },
  {
    icon: Youtube,
    title: "YouTube & Display Retargeting",
    copy: "Demand capture paired with demand reminder — sequential video and display that keeps your brand in the consideration set.",
  },
];

const STACK = [
  {
    icon: Download,
    title: "Offline Conversion Imports",
    copy: "Closed deals flow back to Google so Smart Bidding optimizes toward revenue, not form fills.",
  },
  {
    icon: Code,
    title: "Custom Bidding Scripts",
    copy: "Automation layered on automation — bidding rules, budget guards and alerts tuned to your unit economics.",
  },
  {
    icon: ShieldCheck,
    title: "Negative Keyword Shields",
    copy: "Relentless query hygiene. Every wasted impression blocked today compounds into savings tomorrow.",
  },
];

const FIXES = [
  {
    icon: Target,
    title: "Match-type discipline",
    copy: "Budget pointed at buying intent, not query noise.",
  },
  {
    icon: Search,
    title: "Query hygiene",
    copy: "Wasted impressions blocked continuously, so savings compound.",
  },
  {
    icon: Package,
    title: "Asset coverage",
    copy: "Performance Max fed the breadth and quality of signals it needs.",
  },
  {
    icon: ShieldCheck,
    title: "Conversion feedback",
    copy: "Closed deals flowing back so Smart Bidding optimizes toward revenue.",
  },
];

const FAQ_ITEMS = [
  {
    q: "Search, Shopping or Performance Max — where do we start?",
    a: "Depends on intent volume and margin. We audit your query landscape and unit economics first, then allocate budget where intent is cheapest to win.",
  },
  {
    q: "Can you work with our existing PPC team?",
    a: "Yes. Some partners hand us the whole account; others use us as the specialist layer for structure, attribution and scaling decisions.",
  },
  {
    q: "How do you report results?",
    a: "One dashboard, weekly: spend, revenue, ROAS, CPA and the decisions behind them. No 40-slide decks.",
  },
  {
    q: "Do you require long contracts?",
    a: "No. 90-day engagements, renewed because the numbers say so — not because of a lock-in.",
  },
];

const GoogleAds = () => (
  <>
    <PageHero
      eyebrow="Service / 02 — Google Ads (Search, Shopping, PMax)"
      lines={[<>Own the moment</>, <><span className="text-flame">of intent.</span></>]}
      sub="Google captures demand that already exists. We make sure it converts to you — with surgical search structures, Performance Max asset discipline and attribution that survives the black box."
      image={IMG}
      alt="Analytics performance charts on screen"
    />

    <section className="py-20 sm:py-28" data-testid="google-capabilities">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-flame">Core capabilities</p>
          <h2 className="mt-4 font-syne text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            Intent is bought at the keyword level.
          </h2>
        </Reveal>
        <div className="mt-14 grid sm:grid-cols-2 gap-6">
          {CAPABILITIES.map((c, i) => (
            <Reveal key={c.title} delay={0.08 * i} className="h-full">
              <div className="h-full rounded-2xl border border-white/10 bg-surface p-8 transition-colors hover:border-flame/40">
                <div className="h-11 w-11 rounded-xl bg-flame/10 border border-flame/30 flex items-center justify-center">
                  <c.icon className="h-5 w-5 text-flame" />
                </div>
                <h3 className="mt-6 font-syne text-xl font-bold text-white">{c.title}</h3>
                <p className="mt-3 text-sm text-gray-400 leading-relaxed">{c.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 sm:py-28 border-t border-white/10" data-testid="google-stack">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-flame">Technical stack</p>
          <h2 className="mt-4 font-syne text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            The attribution engine behind the bids.
          </h2>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {STACK.map((s, i) => (
            <Reveal key={s.title} delay={0.1 * i} className="h-full">
              <div className="h-full rounded-2xl border border-white/10 bg-surface p-8">
                <div className="h-11 w-11 rounded-xl bg-flame/10 border border-flame/30 flex items-center justify-center">
                  <s.icon className="h-5 w-5 text-flame" />
                </div>
                <h3 className="mt-6 font-syne text-xl font-bold text-white">{s.title}</h3>
                <p className="mt-3 text-sm text-gray-400 leading-relaxed">{s.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 sm:py-28 border-t border-white/10" data-testid="google-restructure">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-flame">Inside the account</p>
            <h2 className="mt-4 font-syne text-3xl sm:text-4xl font-bold tracking-tight text-white">
              Structure is the cheapest efficiency lever there is.
            </h2>
            <p className="mt-5 text-base text-gray-400 leading-relaxed">
              Most accounts leak on match types, query sprawl and asset neglect — issues that a
              disciplined restructure addresses before any additional budget is spent.
            </p>
            <Link
              to="/contact"
              data-testid="google-audit-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-flame px-7 py-3.5 font-semibold text-white transition-all hover:bg-flame-hover"
            >
              Request a Google audit
              <ArrowUpRight className="h-5 w-5" />
            </Link>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <div className="rounded-3xl border border-white/10 bg-surface p-8 sm:p-10">
              <div className="flex items-center justify-between mb-8">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-gray-400">What a restructure addresses</p>
                <p className="font-mono text-xs text-flame">Google Ads</p>
              </div>
              <div className="space-y-7">
                {FIXES.map((f) => (
                  <div key={f.title} className="flex items-start gap-5">
                    <div className="h-11 w-11 shrink-0 rounded-xl bg-flame/10 border border-flame/30 flex items-center justify-center">
                      <f.icon className="h-5 w-5 text-flame" />
                    </div>
                    <div>
                      <h3 className="font-syne text-lg font-bold text-white">{f.title}</h3>
                      <p className="mt-1.5 text-sm text-gray-400 leading-relaxed">{f.copy}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="py-20 sm:py-28 border-t border-white/10" data-testid="google-faq">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-flame">Questions</p>
          <h2 className="mt-4 font-syne text-3xl sm:text-4xl font-bold tracking-tight text-white mb-10">
            Before you scale on Google.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <FAQ items={FAQ_ITEMS} testPrefix="google-faq" />
        </Reveal>
      </div>
    </section>

    <CTABanner
      title="Every auction happens with or without you."
      sub="Book a strategic audit. We'll break down your search terms, structure and bidding — and show you where the margin is hiding."
      testPrefix="google-cta"
    />
  </>
);

export default GoogleAds;
